import { useEffect, useState } from "react";
import * as ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import "@/components/css/picture-viewer.css";

type PictureViewerPropsI = {
    images: string[];
};

export default function PictureViewer({ images }: PictureViewerPropsI) {
    const [isOpen, setIsOpen] = useState(false);
    const [startIndex, setStartIndex] = useState(0);

    return (
        <>
            <div
                style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {images.map((src, i) => (
                    <img
                        className="picture-viewer"
                        key={i}
                        src={src}
                        alt={`Fotoğraf ${i + 1}`}
                        style={{
                            width: 130,
                            height: 80,
                            objectFit: "cover",
                            cursor: "pointer",
                            borderRadius: 8,
                            objectPosition: "center 70%",
                        }}
                        onClick={() => {
                            setStartIndex(i);
                            setIsOpen(true);
                        }}
                    />
                ))}
            </div>

            {isOpen && (
                <FullscreenSliderModal
                    images={images}
                    startIndex={startIndex}
                    onClose={() => setIsOpen(false)}
                />
            )}
        </>
    );
}
function FullscreenSliderModal({
    images,
    startIndex,
    onClose,
}: {
    images: string[];
    startIndex: number;
    onClose: () => void;
}) {
    const [[currentIndex, direction], setIndex] = useState<[number, number]>([
        startIndex,
        0,
    ]);
    const [isDragging, setIsDragging] = useState(false);

    const variants = {
        enter: (dir: number) => ({
            x: dir > 0 ? 300 : -300,
            opacity: 0,
            scale: 0.8,
        }),
        center: { x: 0, opacity: 1, scale: 1 },
        exit: (dir: number) => ({
            x: dir < 0 ? 300 : -300,
            opacity: 0,
            scale: 0.8,
        }),
    };

    const changeIndex = (newIndex: number, dir: number) => {
        setIndex([newIndex, dir]);
    };

    const prev = (e?: React.MouseEvent | KeyboardEvent) => {
        e?.stopPropagation?.();
        changeIndex((currentIndex - 1 + images.length) % images.length, -1);
    };

    const next = (e?: React.MouseEvent | KeyboardEvent) => {
        e?.stopPropagation?.();
        changeIndex((currentIndex + 1) % images.length, 1);
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") prev(e);
            else if (e.key === "ArrowRight") next(e);
            else if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [currentIndex]);

    return ReactDOM.createPortal(
        <div
            onClick={() => {
                if (!isDragging) onClose();
            }}
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0,0,0,0.8)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
                cursor: "zoom-out",
                overflow: "hidden",
            }}
        >
            <button
                onClick={prev}
                style={{
                    position: "absolute",
                    left: 20,
                    fontSize: 30,
                    color: "white",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    userSelect: "none",
                }}
                aria-label="Önceki"
            >
                ‹
            </button>

            <AnimatePresence initial={false} custom={direction}>
                <motion.img
                    key={currentIndex}
                    src={images[currentIndex]}
                    alt={`Fotoğraf ${currentIndex + 1}`}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.25}
                    onDragStart={() => setIsDragging(true)}
                    onDragEnd={(e, info) => {
                        const swipeThreshold = 50; // Minimum sürükleme mesafesi
                        setIsDragging(false);
                        if (info.offset.x > swipeThreshold) prev();
                        else if (info.offset.x < -swipeThreshold) next();
                    }}
                    style={{
                        maxHeight: "90%",
                        maxWidth: "90%",
                        objectFit: "contain",
                        borderRadius: 10,
                        userSelect: "none",
                        cursor: "grab",
                        position: "absolute",
                    }}
                />
            </AnimatePresence>

            <button
                onClick={next}
                style={{
                    position: "absolute",
                    right: 20,
                    fontSize: 30,
                    color: "white",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    userSelect: "none",
                }}
                aria-label="Sonraki"
            >
                ›
            </button>
        </div>,
        document.body
    );
}
